import * as Api from '@ViewModels';
import { ComposeEmailViewModel, ContactViewModel } from '../../../../viewmodels/AppViewModels';

/**
 * Extracts tag names from the reach out context, prioritizing tagsWithAlerts over countByTag.
 *
 * @param context - The reach out context from the daily updates endpoint
 * @returns An array of tag names, or an empty array if no tags are found
 */
export function extractTagNamesFromReachOutContext(context?: Api.IReachOutInfo): string[] {
	if (!context || context.tagAlertCount === 0) {
		return [];
	}

	// Prioritize tagsWithAlerts if available and non-empty
	if (context.tagsWithAlerts && context.tagsWithAlerts.length > 0) {
		return context.tagsWithAlerts;
	}

	// Fallback to countByTag
	if (context.countByTag && context.countByTag.length > 0) {
		return context.countByTag.map(t => t.tag);
	}

	return [];
}

/**
 * Builds filter criteria for tag alerts based on tag names.
 * Creates a filter structure that includes:
 * 1. TagAlert property filter (contacts with alerts due)
 * 2. Tag filters with OR logic (Tag=X OR Tag=Y OR Tag=Z)
 *
 * @param tagNames - Array of tag names to filter by
 * @returns An array of contact filter criteria, or an empty array if no tags provided
 */
export function buildTagAlertFilterCriteria(tagNames: string[]): Api.IContactFilterCriteria[] {
	if (tagNames.length === 0) {
		return [];
	}

	const filterCriteria: Api.IContactFilterCriteria[] = [];

	// Build tag filters: (Tag=X OR Tag=Y OR Tag=Z)
	const tagFilters: Api.IContactFilterCriteria[] = tagNames.map(tagName => ({
		property: Api.ContactFilterCriteriaProperty.Tag,
		value: tagName,
	}));

	// Add TagAlert property filter (contacts with alerts due)
	filterCriteria.push({
		property: Api.ContactFilterCriteriaProperty.TagAlert,
	});

	// Add tag filters with OR
	if (tagFilters.length > 1) {
		filterCriteria.push({
			op: Api.FilterOperator.Or,
			criteria: tagFilters,
		});
	} else if (tagFilters.length === 1) {
		filterCriteria.push(tagFilters[0]);
	}

	return filterCriteria;
}

/**
 * Configures the email composer with reach out filters based on the daily updates context.
 * Handles two scenarios:
 * 1. KeepInTouch only: Adds contacts directly to includeContactIds (no filter needed)
 * 2. Tag alerts present: Builds and applies filter criteria
 *
 * The API returns ALL keepInTouchContacts, so they can be added directly.
 * Tag alert contacts are only a sample, so we use filter criteria to get all matching contacts.
 *
 * @param emailComposer - The email composer to configure
 * @param context - The reach out context from the daily updates endpoint
 * @param contactsWithKeepInTouchesDue - The full list of contacts with keep in touches due
 */
export function applyReachOutFiltersToEmailComposer(
	emailComposer: ComposeEmailViewModel<any, any>,
	context: Api.IReachOutInfo | undefined,
	contactsWithKeepInTouchesDue: ContactViewModel[]
): void {
	// Handle KeepInTouch contacts: Add to contactsToAdd (maps to includeContactIds)
	// The API returns ALL keepInTouchContacts, so we can use them directly
	if (context?.keepInTouchCount && context.keepInTouchCount > 0 && contactsWithKeepInTouchesDue.length > 0) {
		emailComposer.emailMessage.contactsToAdd.addAll(contactsWithKeepInTouchesDue);
	}

	// Build filter criteria for tag alerts if present
	// Tag alert contacts from API are only a sample, so we need to use filter criteria
	const tagNames = extractTagNamesFromReachOutContext(context);
	const filterCriteria = buildTagAlertFilterCriteria(tagNames);

	// Apply filter criteria only if we have tag alerts
	// When using includeContactIds alone (KeepInTouch only), no filter is needed
	if (filterCriteria.length > 0) {
		// Tag alerts present: use filter criteria with exclusions
		emailComposer.emailMessage.contactsFilterRequest = {
			contactFilterRequest: {
				op: Api.FilterOperator.And,
				criteria: [...filterCriteria, ...Api.DefaultBulkSendExcludedFilterCriteria],
			},
		};
	}
}
